// Shared membership/subscription plan data — used by the home page teaser
// section, the full Subscription Plans page, and the membership checkout
// flow, so all three stay in sync.
//
// Every plan visit is a fixed 2-hour slot (see TIME_SLOTS). A plan's
// `price` / `period` / `durationDays` / `visitsCount` are its CANONICAL
// purchase terms — what actually gets bought and sent to the backend.
// The weekly/monthly/yearly toggle on the display pages is a "compare
// pricing" view only; it does not change what a purchase actually buys.
//
// Images all come from the standard registry in src/config/serviceImages.js
// (local assets in components/images/stage_01/) — no external stock photos.

import { getPlanHeroImages } from "../../config/serviceImages";
import { MEMBERSHIP_PLAN_PRICES } from "../../config/pricing";

export const BILLING_CYCLES = ["weekly", "monthly", "yearly"];

// The 2-hour visit windows a customer can pick as their recurring slot —
// default grid, used by every plan except ones with their own override
// below (see MONTHLY_CARE_TIME_SLOTS / getTimeSlotsForPlan).
export const TIME_SLOTS = [
  { id: "slot-1", label: "7:00 AM - 9:00 AM" },
  { id: "slot-2", label: "9:15 AM - 11:15 AM" },
  { id: "slot-3", label: "11:30 AM - 1:30 PM" },
  { id: "slot-4", label: "2:00 PM - 4:00 PM" },
  { id: "slot-5", label: "4:15 PM - 6:15 PM" },
  { id: "slot-6", label: "6:30 PM - 8:30 PM" },
];

// "monthly-care" gets its own denser, staggered slot grid — 12 windows
// with overlapping start times (mirrors the shift-overlap pattern already
// used by the Schedule Booking wizard's per-category slot templates).
// Mirrored in K2Karmika-AppBackend/labour_service/views.py as
// MONTHLY_CARE_TIME_SLOTS — ids/labels must stay identical on both sides
// since the frontend sends slot_id back at purchase time.
export const MONTHLY_CARE_TIME_SLOTS = [
  { id: "slot-1",  label: "6:00 AM - 8:00 AM" },
  { id: "slot-2",  label: "6:30 AM - 8:30 AM" },
  { id: "slot-3",  label: "8:30 AM - 10:30 AM" },
  { id: "slot-4",  label: "9:00 AM - 11:00 AM" },
  { id: "slot-5",  label: "11:00 AM - 1:00 PM" },
  { id: "slot-6",  label: "11:30 AM - 1:30 PM" },
  { id: "slot-7",  label: "1:30 PM - 3:30 PM" },
  { id: "slot-8",  label: "2:00 PM - 4:00 PM" },
  { id: "slot-9",  label: "4:00 PM - 6:00 PM" },
  { id: "slot-10", label: "4:30 PM - 6:30 PM" },
  { id: "slot-11", label: "6:30 PM - 8:30 PM" },
  { id: "slot-12", label: "7:00 PM - 9:00 PM" },
];

// Bronze Package visits are 2 hours 15 minutes, not the plain 2-hour
// window every other plan on this staggered grid uses — same 12 start
// times as MONTHLY_CARE_TIME_SLOTS (2 partner tracks × 6 slots each),
// just 15 minutes longer per window. Mirrored in
// K2Karmika-AppBackend/labour_service/views.py as BRONZE_PACKAGE_TIME_SLOTS
// — ids/labels must stay identical on both sides since the frontend sends
// slot_id back at purchase time.
export const BRONZE_PACKAGE_TIME_SLOTS = [
  { id: "slot-1",  label: "6:00 AM - 8:15 AM" },
  { id: "slot-2",  label: "6:30 AM - 8:45 AM" },
  { id: "slot-3",  label: "8:30 AM - 10:45 AM" },
  { id: "slot-4",  label: "9:00 AM - 11:15 AM" },
  { id: "slot-5",  label: "11:00 AM - 1:15 PM" },
  { id: "slot-6",  label: "11:30 AM - 1:45 PM" },
  { id: "slot-7",  label: "1:30 PM - 3:45 PM" },
  { id: "slot-8",  label: "2:00 PM - 4:15 PM" },
  { id: "slot-9",  label: "4:00 PM - 6:15 PM" },
  { id: "slot-10", label: "4:30 PM - 6:45 PM" },
  { id: "slot-11", label: "6:30 PM - 8:45 PM" },
  { id: "slot-12", label: "7:00 PM - 9:15 PM" },
];

// Silver Package visits are 2 hours 30 minutes, not the plain 2-hour
// window every other plan on this staggered grid uses — same 12 start
// times as MONTHLY_CARE_TIME_SLOTS (2 partner tracks × 6 slots each), just
// 30 minutes longer per window. Mirrored in
// K2Karmika-AppBackend/labour_service/views.py as SILVER_PACKAGE_TIME_SLOTS
// — ids/labels must stay identical on both sides since the frontend sends
// slot_id back at purchase time.
export const SILVER_PACKAGE_TIME_SLOTS = [
  { id: "slot-1",  label: "6:00 AM - 8:30 AM" },
  { id: "slot-2",  label: "6:30 AM - 9:00 AM" },
  { id: "slot-3",  label: "8:30 AM - 11:00 AM" },
  { id: "slot-4",  label: "9:00 AM - 11:30 AM" },
  { id: "slot-5",  label: "11:00 AM - 1:30 PM" },
  { id: "slot-6",  label: "11:30 AM - 2:00 PM" },
  { id: "slot-7",  label: "1:30 PM - 4:00 PM" },
  { id: "slot-8",  label: "2:00 PM - 4:30 PM" },
  { id: "slot-9",  label: "4:00 PM - 6:30 PM" },
  { id: "slot-10", label: "4:30 PM - 7:00 PM" },
  { id: "slot-11", label: "6:30 PM - 9:00 PM" },
  { id: "slot-12", label: "7:00 PM - 9:30 PM" },
];

export function getTimeSlotsForPlan(planId) {
  // Bronze (silver-package) gets its own 2-hour-15-minute version of the
  // same 12-window staggered grid — see BRONZE_PACKAGE_TIME_SLOTS above.
  if (planId === "silver-package") return BRONZE_PACKAGE_TIME_SLOTS;
  // Silver (platinum-plus-package) gets its own 2-hour-30-minute version —
  // see SILVER_PACKAGE_TIME_SLOTS above.
  if (planId === "platinum-plus-package") return SILVER_PACKAGE_TIME_SLOTS;
  // "platinum-package" and the rest use the exact same 12-window staggered
  // grid as monthly-care (2 partner tracks × 6 slots each) — same values,
  // so they reuse MONTHLY_CARE_TIME_SLOTS rather than duplicating it.
  if (planId === "monthly-care" || planId === "platinum-package" || planId === "silver-plus-package") return MONTHLY_CARE_TIME_SLOTS;
  // "premium-family" ("Mid-Month Plan") is a plain 2-hour visit — no
  // bespoke grid, same as weekly-care's default.
  return TIME_SLOTS;
}

// Task types covered on every membership visit — the partner covers a
// rotating subset of these each 2-hour visit.
export const CORE_INCLUDED_SERVICES = [
  "Mopping", "Window Cleaning", "Utensils Cleaning",
  "Stove Cleaning",
];

// Average number of the above tasks completed per visit — used to derive
// the plan's total "services included" count (visitsCount * this).
export const SERVICES_PER_VISIT = 3;

// weekly-care's own specific scope of service — distinct from the generic
// CORE_INCLUDED_SERVICES the other two core plans still use.
export const WEEKLY_CARE_INCLUDED_SERVICES = [
  "Kitchen Cleaning", "Utensil Cleaning", "Food Preparation",
  "Sweeping & Mopping", "Dusting & Wiping", "Fan & Ceiling",
  "Bathroom Cleaning", "Furniture Cleaning",
  "Window & Glass / Wall & Door", "Balcony & Entrance Cleaning",
];

export const WEEKLY_CARE_EXCLUDED_SERVICES = [
  "Deep Cleaning", "Heavy Stains", "Heavy Grease", "Furniture Shifting",
  "Appliance Repair", "Plumbing", "Electrical", "Pest Control",
  "Hazardous Waste", "Construction Waste", "Gardening",
  "Specialized Cleaning", "High-Level Areas", "Permanent Damage",
  "Unlisted Services",
];

// Bronze Package's own fixed 17-item scope — distinct from the generic
// CORE_INCLUDED_SERVICES the other core plans use.
export const BRONZE_PACKAGE_INCLUDED_SERVICES = [
  "Kitchen Cleaning", "Utensil Cleaning", "Food Preparation",
  "Sweeping & Mopping", "Dusting & Wiping", "Fan & Ceiling",
  "Bathroom Cleaning", "Furniture Cleaning",
  "Wardrobe Arrangement", "Window & Glass / Wall & Door",
  "Balcony & Entrance Cleaning", "Kitchen Cabinet Cleaning",
  "Packing & Unpacking", "Refrigerator Cleaning",
  "Microwave / Oven / Grill Cleaning", "Display Cabinet / Showcase Cleaning",
];

// Identical exclusion list to Weekly Care's own — reused directly rather
// than duplicated (see WEEKLY_CARE_EXCLUDED_SERVICES above).
export const BRONZE_PACKAGE_EXCLUDED_SERVICES = WEEKLY_CARE_EXCLUDED_SERVICES;

// Free-kit perk per duration tier — SILVER_PACKAGE_DURATIONS in
// config/pricing.js already carries this as each tier's `freeGift`; this
// table is the same data keyed by months, for anywhere that wants to show
// the full kit breakdown independent of the duration-picker UI.
export const BRONZE_PACKAGE_FREE_KIT = {
  1:  "1 × 250 ml Dishwasher Cleaner",
  3:  "2 × 250 ml Dishwasher Cleaner + 1 × 250 ml Toilet Cleaner",
  6:  "3 × 250 ml Dishwasher Cleaner + 1 × 500 ml Toilet Cleaner",
  12: "2 Kits during 12 months — each kit: 4 × 250 ml Dishwasher Cleaner + 1 × 250 ml Toilet Cleaner + 1 × 500 ml Toilet Cleaner",
};

export const BRONZE_PACKAGE_CUSTOMER_NOTES = [
  { label: "Visits", text: "26 visits within each billing period." },
  { label: "Duration", text: "Each visit is limited to 2 hours 15 minutes." },
  { label: "Expiry", text: "Unused visits expire at period end." },
  { label: "Renewal", text: "Subscription auto-renews each billing period." },
  { label: "Cancellation & Refund", text: "Customer may cancel anytime; refund for remaining services follows accepted Terms & Conditions." },
  { label: "Booking", text: "Book slots in advance; changes require 24-hour notice." },
  { label: "Availability", text: "Customer or authorized person must provide safe home access." },
  { label: "Valuables", text: "Secure cash, jewellery, documents, electronics and fragile items." },
  { label: "Existing Damage", text: "Report pre-existing damage before service." },
  { label: "Service Scope", text: "All services follow their defined scope and exclusions." },
  { label: "Safety", text: "Unsafe, inaccessible or technical work areas may be excluded." },
  { label: "Cleaning Kit", text: "Free kit quantity depends on the selected subscription plan." },
  { label: "Important", text: "Subscription cancellation and refunds are subject to the accepted ClickNido Terms & Conditions." },
];

// Gold Package's own fixed 25-item scope — a superset of Bronze's 17, plus
// 8 more categories (Rangoli, Chimney, Steps & Parking, Fish Tank,
// Bed Arrangement, Study/Working Table, Dressing Table, Bedroom Cleaning).
export const GOLD_PACKAGE_INCLUDED_SERVICES = [
  "Kitchen Cleaning", "Utensil Cleaning", "Food Preparation",
  "Sweeping & Mopping", "Dusting & Wiping", "Fan & Ceiling",
  "Bathroom Cleaning", "Furniture Cleaning",
  "Wardrobe Arrangement", "Window & Glass / Wall & Door",
  "Balcony & Entrance Cleaning", "Sand Paint / Floor Art / Rangoli",
  "Kitchen Cabinet Cleaning", "Packing & Unpacking", "Refrigerator Cleaning",
  "Microwave / Oven / Grill Cleaning", "Chimney Cleaning",
  "Display Cabinet / Showcase Cleaning", "Steps & Parking Space Cleaning",
  "Fish Tank Cleaning", "Bed Arrangement", "Study / Working Table Cleaning",
  "Dressing Table Cleaning", "Bedroom Cleaning",
];

// No separate exclusion list was given for Gold — reusing Bronze's (same
// as Weekly Care's) directly rather than duplicating it.
export const GOLD_PACKAGE_EXCLUDED_SERVICES = WEEKLY_CARE_EXCLUDED_SERVICES;

export const GOLD_PACKAGE_CUSTOMER_NOTES = [
  { label: "Visits", text: "26 visits within each billing period." },
  { label: "Duration", text: "Each visit is limited to 3 hours." },
  { label: "Expiry", text: "Unused visits expire at period end." },
  { label: "Renewal", text: "Subscription auto-renews each billing period." },
  { label: "Cancellation & Refund", text: "Customer may cancel anytime; refund for remaining services follows accepted Terms & Conditions." },
  { label: "Booking", text: "Book slots in advance; changes require 24-hour notice." },
  { label: "Availability", text: "Customer or authorized person must provide safe home access." },
  { label: "Valuables", text: "Secure cash, jewellery, documents, electronics and fragile items." },
  { label: "Existing Damage", text: "Report pre-existing damage before service." },
  { label: "Service Scope", text: "All services follow their defined scope and exclusions." },
  { label: "Safety", text: "Unsafe, inaccessible or technical work areas may be excluded." },
  { label: "Cleaning Kit", text: "Free kit quantity depends on the selected subscription plan." },
  { label: "Rapid Service", text: "Free Rapid Post-Party/Post-Fest services apply only to eligible plans." },
  { label: "Important", text: "Subscription cancellation and refunds are subject to the accepted ClickNido Terms & Conditions." },
];

// Platinum's own scope — same 25-item list as Gold (identical Included
// Services were given for both), reused directly rather than duplicated.
export const PLATINUM_PACKAGE_INCLUDED_SERVICES = GOLD_PACKAGE_INCLUDED_SERVICES;
export const PLATINUM_PACKAGE_EXCLUDED_SERVICES = GOLD_PACKAGE_EXCLUDED_SERVICES;

export const PLATINUM_PACKAGE_CUSTOMER_NOTES = [
  { label: "Visits", text: "26 visits within each billing period." },
  { label: "Duration", text: "Each regular visit is limited to 3 hours." },
  { label: "Expiry", text: "Unused visits expire at period end." },
  { label: "Renewal", text: "Subscription auto-renews each billing period." },
  { label: "Cancellation & Refund", text: "Customer may cancel anytime; refund for remaining services follows accepted Terms & Conditions." },
  { label: "Booking", text: "Book slots in advance; changes require 24-hour notice." },
  { label: "Availability", text: "Customer or authorized person must provide safe home access." },
  { label: "Valuables", text: "Secure cash, jewellery, documents, electronics and fragile items." },
  { label: "Existing Damage", text: "Report pre-existing damage before service." },
  { label: "Service Scope", text: "All services follow their defined scope and exclusions." },
  { label: "Safety", text: "Unsafe, inaccessible or technical work areas may be excluded." },
  { label: "Cleaning Kit", text: "Free kit quantity depends on the selected subscription plan." },
  { label: "Rapid Service", text: "Free Rapid services apply only to eligible plans." },
  { label: "Rapid Duration", text: "Each free Rapid service is limited to 2 hours." },
  { label: "Important", text: "Subscription cancellation and refunds are subject to the accepted ClickNido Terms & Conditions." },
];

// Silver's own scope — Bronze's exact 17 items plus Fish Tank Cleaning.
export const SILVER_PACKAGE_INCLUDED_SERVICES = [...BRONZE_PACKAGE_INCLUDED_SERVICES, "Fish Tank Cleaning"];
export const SILVER_PACKAGE_EXCLUDED_SERVICES = WEEKLY_CARE_EXCLUDED_SERVICES;

export const SILVER_PACKAGE_CUSTOMER_NOTES = [
  { label: "Visits", text: "26 visits within each billing period." },
  { label: "Duration", text: "Each visit is limited to 2 hours 30 minutes." },
  { label: "Expiry", text: "Unused visits expire at period end." },
  { label: "Renewal", text: "Subscription auto-renews each billing period." },
  { label: "Cancellation & Refund", text: "Customer may cancel anytime; refund for remaining services follows accepted Terms & Conditions." },
  { label: "Booking", text: "Book slots in advance; changes require 24-hour notice." },
  { label: "Availability", text: "Customer or authorized person must provide safe home access." },
  { label: "Valuables", text: "Secure cash, jewellery, documents, electronics and fragile items." },
  { label: "Existing Damage", text: "Report pre-existing damage before service." },
  { label: "Service Scope", text: "All services follow their defined scope and exclusions." },
  { label: "Safety", text: "Unsafe, inaccessible or technical work areas may be excluded." },
  { label: "Cleaning Kit", text: "Free kit quantity depends on the selected subscription plan." },
  { label: "Important", text: "Subscription cancellation and refunds are subject to the accepted ClickNido Terms & Conditions." },
];

export const WEEKLY_CARE_CUSTOMER_NOTES = [
  { label: "Service Usage", text: "Customer can use the package on any 6 days within the 7-day package period." },
  { label: "6 Days Only", text: "One day remains unused/rest day." },
  { label: "Slot Change", text: "Slot changes must be requested at least 24 hours before the scheduled slot." },
  { label: "Daily Reminder", text: "ClickNido will send a daily reminder for the scheduled service." },
  { label: "Customer Availability", text: "Customer must be available or provide authorized access during the selected slot." },
  { label: "Valuables", text: "Customer must securely store cash, jewellery, documents, electronics and other valuables." },
  { label: "Existing Damage", text: "Customer must inform the professional about any pre-existing damage or broken items." },
  { label: "Professional Responsibility", text: "Professionals perform services within the defined scope; customers are responsible for securing valuables and personal belongings." },
  { label: "Access", text: "Customer must provide safe and reasonable access to the service area." },
  { label: "Service Scope", text: "Services are provided according to the applicable service scope and exclusions." },
  { label: "Unsafe Areas", text: "Unsafe, inaccessible or technically specialized areas may be excluded." },
];

// Mid-Month Plan's own fixed 13-item scope — Weekly Care's exact 11 items
// plus 2 more (Packing & Unpacking, Refrigerator Cleaning).
export const MID_MONTH_PLAN_INCLUDED_SERVICES = [
  ...WEEKLY_CARE_INCLUDED_SERVICES, "Packing & Unpacking", "Refrigerator Cleaning",
];

// Identical exclusion list to Weekly Care's own — reused directly rather
// than duplicated (see WEEKLY_CARE_EXCLUDED_SERVICES above).
export const MID_MONTH_PLAN_EXCLUDED_SERVICES = WEEKLY_CARE_EXCLUDED_SERVICES;

export const MID_MONTH_PLAN_CUSTOMER_NOTES = [
  { label: "Service Usage", text: "Customer can use the package on any 14 days within the 18-day package period." },
  { label: "Rest Days", text: "4 days remain unused/rest days." },
  { label: "Duration", text: "Each visit is limited to 2 hours." },
  { label: "Slot Change", text: "Slot changes must be requested at least 24 hours before the scheduled slot." },
  { label: "Daily Reminder", text: "ClickNido will send a daily reminder for the scheduled service." },
  { label: "Customer Availability", text: "Customer must be available or provide authorized access during the selected slot." },
  { label: "Valuables", text: "Customer must securely store cash, jewellery, documents, electronics and other valuables." },
  { label: "Existing Damage", text: "Customer must inform the professional about any pre-existing damage or broken items." },
  { label: "Packing & Unpacking", text: "Packing & Unpacking is limited to regular household items; valuables and fragile items must be handled by the customer." },
  { label: "Refrigerator Cleaning", text: "Refrigerator Cleaning covers the interior and exterior only; defrosting and repairs are not included." },
  { label: "Professional Responsibility", text: "Professionals perform services within the defined scope; customers are responsible for securing valuables and personal belongings." },
  { label: "Access", text: "Customer must provide safe and reasonable access to the service area." },
  { label: "Service Scope", text: "Services are provided according to the applicable service scope and exclusions." },
  { label: "Cancellation & Refund", text: "Customer may cancel anytime; refund for remaining services follows accepted Terms & Conditions." },
];

// Core 3 plans — shown on the home page teaser and also included in the
// full catalog on the Subscription Plans page.
export const CORE_PLANS = [
  {
    id: "weekly-care",
    name: "Weekly Care",
    tagline: "Perfect for regular upkeep",
    icon: "Package",
    price: MEMBERSHIP_PLAN_PRICES["weekly-care"],
    period: "/week",
    // 6 visits inside a 7-day validity window — customer uses any 6 of the
    // 7 days, the 7th is a flexible rest/buffer day (see WEEKLY_CARE_
    // CUSTOMER_NOTES "6 Days Only"). Backend mirrors this: subscription_weekly
    // only creates 6 day-records (visits_total), not 7 (duration_days) — see
    // purchase_membership()'s "weekly" in plan_id branch in views.py.
    durationDays: 7,
    visitsCount: 6,
    visitHours: 2,
    images: getPlanHeroImages("weekly-care"),
    // This plan's included-services list is a fixed, enumerated scope (11
    // named tasks) rather than a derived visitsCount*SERVICES_PER_VISIT
    // estimate — the Details modal shows includedServices.length directly
    // instead of the computed figure when this is set.
    servicesAreDefinitive: true,
    includedServices: WEEKLY_CARE_INCLUDED_SERVICES,
    excludedServices: WEEKLY_CARE_EXCLUDED_SERVICES,
    customerNotes: WEEKLY_CARE_CUSTOMER_NOTES,
    dailyRoutineNote: "Regular daily cleaning and basic household assistance within the 2-hour visit.",
    packageSummary: "7 Days • 6 Visits • 2 Hours Per Day",
    features: [
      "6 Home Service Visits – 2 Hours Each",
      "7-Day Package Validity",
      "Flexible Cancellation",
      "Exclusive Package Discounts",
    ],
    cta: "Start Weekly Plan",
    highlight: false,
  },
  {
    id: "monthly-care",
    name: "Monthly Care",
    tagline: "Best value for families",
    icon: "Home",
    price: MEMBERSHIP_PLAN_PRICES["monthly-care"],
    period: "/month",
    durationDays: 30,   // full 30-day validity window; the 24 visits are spread across it
                        // with a rest day after every 5 consecutive services (see backend
                        // _generate_working_dates) — 4 rest days land inside this window
    visitsCount: 24,
    images: getPlanHeroImages("monthly-care"),
    includedServices: CORE_INCLUDED_SERVICES,
    features: [
      "24 Service Visits / Month (2 hrs each)",
      "10% Discount on Services",
      "Free Rescheduling",
      "Priority Support",
      "Member Pricing",
    ],
    cta: "Start Monthly Plan",
    highlight: true,
  },
  {
    // No bespoke checkout page — falls through to the generic
    // MembershipCheckout.js catch-all route, same as weekly-care/
    // monthly-care (fixed single package, no duration picker, no addons).
    id: "premium-family",
    name: "Mid-Month Plan",
    tagline: "14 visits every 18 days",
    icon: "Gem",
    price: MEMBERSHIP_PLAN_PRICES["premium-family"],
    period: "/18 days",
    // 14 visits inside an 18-day validity window — 3 consecutive work days,
    // 1 rest day, repeating (see backend _generate_working_dates with
    // work_streak=3/rest_days=1 in purchase_membership()).
    durationDays: 18,
    visitsCount: 14,
    visitHours: 2,
    images: getPlanHeroImages("premium-family"),
    // Fixed, enumerated 13-item scope — see the Weekly Care plan above for
    // the same servicesAreDefinitive pattern.
    servicesAreDefinitive: true,
    includedServices: MID_MONTH_PLAN_INCLUDED_SERVICES,
    excludedServices: MID_MONTH_PLAN_EXCLUDED_SERVICES,
    customerNotes: MID_MONTH_PLAN_CUSTOMER_NOTES,
    dailyRoutineNote: "Regular daily cleaning and basic household assistance within the 2-hour visit.",
    packageSummary: "18 Days • 14 Visits • 2 Hours Per Day",
    features: [
      "14 Service Visits / 18 Days (2 hrs each)",
      "Flexible Cancellation",
      "Exclusive Package Discounts",
    ],
    cta: "Start Mid-Month Plan",
    highlight: false,
  },
];

// Extra tiered packages — only shown on the full Subscription Plans page.
export const EXTRA_PLANS = [
  {
    // Its own dedicated checkout flow (SilverPackageCheckout.js — file/id/
    // route/backend collection all stay "silver"; only the customer-facing
    // name changed to Bronze). price/period/durationDays/visitsCount below
    // are only for the listing-page "starting from" preview; the real
    // duration choice (1/3/6/12 months) happens on the checkout page. See
    // SILVER_PACKAGE_DURATIONS / SILVER_PACKAGE_SERVICES_PER_MONTH in
    // config/pricing.js.
    id: "silver-package",
    name: "Bronze Package",
    tagline: "26 visits every month, 2 hrs 15 min per visit",
    icon: "Award",
    price: 6999,         // starting-from price — 1-month tier (placeholder, see pricing.js)
    period: "/month",
    durationDays: 30,
    visitsCount: 26,
    images: getPlanHeroImages("silver-package"),
    // Fixed, enumerated 17-item scope — see the Weekly Care plan above for
    // the same servicesAreDefinitive pattern.
    servicesAreDefinitive: true,
    // Details modal shows image + Services Included/Excluded + Plan
    // Benefits only — no Plan Duration/Visits/Services Total stat row.
    hideDetailStats: true,
    includedServices: BRONZE_PACKAGE_INCLUDED_SERVICES,
    excludedServices: BRONZE_PACKAGE_EXCLUDED_SERVICES,
    customerNotes: BRONZE_PACKAGE_CUSTOMER_NOTES,
    dailyRoutineNote: "Regular daily cleaning and basic household assistance within the 2 hours 15 minutes visit.",
    packageSummary: "30 Days • 26 Visits • 2 Hrs 15 Min Per Visit",
    features: [
      "26 Service Visits / Month (2 hrs 15 min each)",
      "2 Partners — Flexible Slot Timings",
      "Choose 1, 3, 6 or 12 Month Plans",
      "Free Product Kit on Every Tier",
      "Max Discount on Services",
    ],
    cta: "Choose Bronze",
    highlight: false,
  },
  {
    // Its own dedicated checkout flow (SilverPlusPackageCheckout.js — file/
    // id/route/backend collection all stay "silver-plus"; only the
    // customer-facing name changed to Gold). No longer the double-visit
    // model Silver Plus used to be — see SILVER_PLUS_PACKAGE_DURATIONS /
    // SILVER_PLUS_PACKAGE_SERVICES_PER_MONTH in config/pricing.js.
    // price/period/durationDays/visitsCount below are only for the
    // listing-page "starting from" preview; the real duration choice
    // (1/3/6/12 months) happens on the checkout page.
    id: "silver-plus-package",
    name: "Gold Package",
    tagline: "26 visits every month, 3 hrs per visit",
    icon: "Award",
    price: 13999,        // starting-from price — 1-month tier (placeholder, see pricing.js)
    period: "/month",
    durationDays: 30,
    visitsCount: 26,
    images: getPlanHeroImages("silver-plus-package"),
    // Fixed, enumerated 25-item scope — see Bronze Package above for the
    // same servicesAreDefinitive pattern.
    servicesAreDefinitive: true,
    includedServices: GOLD_PACKAGE_INCLUDED_SERVICES,
    excludedServices: GOLD_PACKAGE_EXCLUDED_SERVICES,
    customerNotes: GOLD_PACKAGE_CUSTOMER_NOTES,
    dailyRoutineNote: "Regular daily cleaning and basic household assistance within the 3-hour visit.",
    packageSummary: "30 Days • 26 Visits • 3 Hrs Per Visit",
    features: [
      "26 Service Visits / Month (3 hrs each)",
      "2 Partners — Flexible Slot Timings",
      "Choose 1, 3, 6 or 12 Month Plans",
      "Free Product Kit on Every Tier",
      "Free Rapid Post-Party/Post-Fest Service on 6+ Month Plans",
      "Max Discount on Services",
    ],
    cta: "Choose Gold",
    highlight: false,
  },
  {
    // Its own dedicated checkout flow (PlatinumPackageCheckout.js) — like
    // silver-package, price/period/durationDays/visitsCount
    // below are only for the listing-page "starting from" preview; the
    // real duration choice (30 days / 3 / 6 / 12 months) happens on the
    // checkout page. See PLATINUM_PACKAGE_DURATIONS /
    // PLATINUM_PACKAGE_SERVICES_PER_MONTH in config/pricing.js.
    id: "platinum-package",
    name: "Platinum",
    tagline: "26 visits every month, 3 hrs per visit",
    icon: "Gem",
    price: 11709,        // starting-from price — 30-day tier
    period: "/month",
    durationDays: 30,
    visitsCount: 26,
    images: getPlanHeroImages("platinum-package"),
    // Fixed, enumerated 25-item scope — see Bronze/Gold above for the same
    // servicesAreDefinitive pattern.
    servicesAreDefinitive: true,
    // Details modal shows image + Services Included/Excluded + Plan
    // Benefits only — no price or Plan Duration/Visits/Services Total stat
    // row (same as Bronze/Gold).
    hideDetailStats: true,
    includedServices: PLATINUM_PACKAGE_INCLUDED_SERVICES,
    excludedServices: PLATINUM_PACKAGE_EXCLUDED_SERVICES,
    customerNotes: PLATINUM_PACKAGE_CUSTOMER_NOTES,
    dailyRoutineNote: "Regular daily cleaning and basic household assistance within the 3-hour visit.",
    packageSummary: "30 Days • 26 Visits • 3 Hrs Per Visit",
    features: [
      "26 Service Visits / Month (3 hrs each)",
      "2 Partners — Flexible Slot Timings",
      "Choose 1, 3, 6 or 12 Month Plans",
      "Free Rapid Pre/Post-Party / Pre/Post-Fest Service on 3+ Month Plans",
      "Free Product Kit on Every Tier",
      "Max Discount on Services",
    ],
    cta: "Choose Platinum",
    highlight: false,
  },
  {
    // Its own dedicated checkout flow (PlatinumPlusPackageCheckout.js —
    // file/id/route/backend collection all stay "platinum-plus"; only the
    // customer-facing name changed to Silver). No longer the double-visit
    // model Platinum Plus used to be — see PLATINUM_PLUS_PACKAGE_DURATIONS /
    // PLATINUM_PLUS_PACKAGE_SERVICES_PER_MONTH in config/pricing.js.
    // price/period/durationDays/visitsCount below are only for the
    // listing-page "starting from" preview; the real duration choice
    // (1/3/6/12 months) happens on the checkout page.
    id: "platinum-plus-package",
    name: "Silver",
    tagline: "26 visits every month, 2 hrs 30 min per visit",
    icon: "Gem",
    price: 23418,        // starting-from price — 1-month tier
    period: "/month",
    durationDays: 30,
    visitsCount: 26,
    images: getPlanHeroImages("platinum-plus-package"),
    // Fixed, enumerated 18-item scope — see Bronze/Gold/Platinum above for
    // the same servicesAreDefinitive pattern.
    servicesAreDefinitive: true,
    // Details modal shows image + Services Included/Excluded + Plan
    // Benefits only — no price or Plan Duration/Visits/Services Total stat
    // row (same as Bronze/Platinum).
    hideDetailStats: true,
    includedServices: SILVER_PACKAGE_INCLUDED_SERVICES,
    excludedServices: SILVER_PACKAGE_EXCLUDED_SERVICES,
    customerNotes: SILVER_PACKAGE_CUSTOMER_NOTES,
    dailyRoutineNote: "Regular daily cleaning and basic household assistance within the 2 hours 30 minutes visit.",
    packageSummary: "30 Days • 26 Visits • 2 Hrs 30 Min Per Visit",
    features: [
      "26 Service Visits / Month (2 hrs 30 min each)",
      "2 Partners — Flexible Slot Timings",
      "Choose 1, 3, 6 or 12 Month Plans",
      "Free Product Kit on Every Tier",
      "Max Discount on Services",
    ],
    cta: "Choose Silver",
    highlight: false,
  },
];

export const ALL_PLANS = [...CORE_PLANS, ...EXTRA_PLANS];

export function getPlanById(planId) {
  return ALL_PLANS.find((p) => p.id === planId) || null;
}

// ── Display-only helpers for the weekly/monthly/yearly comparison toggle ──
// (purchases always use the plan's own canonical price/period/duration).
function getMonthlyEquivalent(plan) {
  return plan.period === "/week" ? plan.price * 4 : plan.price;
}

export function getCyclePrice(plan, cycle) {
  const monthly = getMonthlyEquivalent(plan);
  if (cycle === "weekly") return Math.round(monthly / 4 / 10) * 10;
  if (cycle === "yearly") return monthly * 10; // 2 months free
  return monthly;
}

export function getCyclePeriod(cycle) {
  if (cycle === "weekly") return "/week";
  if (cycle === "yearly") return "/year";
  return "/month";
}

export function formatInr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

export function formatVisits(visitsCount) {
  return visitsCount == null ? "Unlimited Visits" : `${visitsCount} Visits`;
}

// Total individual task completions across the plan (visits * tasks/visit).
// null (unlimited plans) stays null — there's no fixed total to show.
export function getTotalServicesIncluded(plan) {
  return plan.visitsCount == null ? null : plan.visitsCount * SERVICES_PER_VISIT;
}

export function formatServicesIncluded(plan) {
  const total = getTotalServicesIncluded(plan);
  return total == null ? "Unlimited" : `${total} Services`;
}

// 'YYYY-MM-DD' + N days -> 'YYYY-MM-DD' (client-side mirror of the backend's
// _add_days, used to preview the end date before the purchase is made).
export function addDays(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}
